import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { array } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { Section, VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import { Button, Chip } from 'tio-ui/components/buttons';
import {
  FilterTabs,
  MatchScoreBadge,
  SkillChip,
  StatCard,
  TaEligibilityBadge,
  ActionableCard,
} from 'tio-ui/components/patterns';
import { gte } from 'tio-ui/utilities';
import {
  PhArrowLeft,
  PhCode,
  PhCube,
  PhGridFour,
  PhLayout,
  PhTarget,
  PhTrendUp,
  PhCurrencyDollar,
  PhCheckCircle,
  PhGraduationCap,
  PhArrowRight,
  PhHeart,
  PhBriefcase,
  PhClock,
  PhSparkle,
} from 'ember-phosphor-icons';

/**
 * This page showcases the UI patterns developed for Career Pathways
 * that could be extracted into tio-ui as reusable components.
 *
 * CANDIDATE COMPONENTS FOR TIO-UI:
 * 1. FilterTabs - Horizontal pill-style filter buttons
 * 2. MatchScoreBadge - Circular percentage display with color coding
 * 3. StatCard - Metric display with icon and optional trend
 * 4. SkillChip - Skill tag with optional level indicator
 * 5. StatusBadge - Consistent status indicators
 * 6. ProgressCircle - SVG circular progress indicator
 * 7. TaEligibilityBadge - TA eligibility status
 * 8. ReviewStars - Star rating display
 * 9. ComparisonCard - Side-by-side comparison layout
 * 10. TimelineNode - Roadmap/timeline element
 */

// Demo data
const SAMPLE_TABS = [
  { id: 'all', label: 'All', count: 24 },
  { id: 'recommended', label: 'Recommended', count: 8 },
  { id: 'in-progress', label: 'In Progress', count: 3 },
  { id: 'completed', label: 'Completed', count: 5 },
];

const SAMPLE_SKILLS = [
  { name: 'Leadership', level: 85, category: 'Management' },
  { name: 'Patient Care', level: 95, category: 'Clinical' },
  { name: 'Epic EMR', level: 72, category: 'Technical' },
  { name: 'Budget Management', level: 45, category: 'Management' },
];

export default class CareerPathwaysComponentShowcase extends Component {
  @tracked activeTab = 'all';
  @tracked likedItems: number[] = [];

  setActiveTab = (tabId: string) => {
    this.activeTab = tabId;
  };

  toggleLike = (itemId: number) => {
    if (this.likedItems.includes(itemId)) {
      this.likedItems = this.likedItems.filter((id) => id !== itemId);
    } else {
      this.likedItems = [...this.likedItems, itemId];
    }
  };

  isLiked = (itemId: number) => {
    return this.likedItems.includes(itemId);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      {{! Back navigation }}
      <div class="mb-4">
        <LinkTo
          @route="authenticated.reimagined"
          class="inline-flex items-center gap-2 text-violet-600 hover:text-violet-800 font-medium"
        >
          <PhArrowLeft @size={{18}} />
          Back to Career Pathways
        </LinkTo>
      </div>

      {{! Header }}
      <div class="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-8 text-white mb-6">
        <div class="flex items-center gap-3 mb-3">
          <PhCube @size={{32}} />
          <Heading @level={{1}} class="text-white text-2xl">UI Component Patterns</Heading>
        </div>
        <Text class="text-gray-300 text-lg max-w-3xl">
          This page showcases UI patterns developed for Career Pathways that are candidates for
          extraction into tio-ui. These patterns ensure consistency across the feature and could
          benefit other parts of the application.
        </Text>
        <div class="flex gap-4 mt-4">
          <Chip @intent="primary">
            <PhCode @size={{14}} class="mr-1" />
            6 Patterns
          </Chip>
          <Chip @intent="success">
            <PhCheckCircle @size={{14}} class="mr-1" />
            Production Ready
          </Chip>
        </div>
      </div>

      <div class="space-y-8">
        {{! ========================================
            PATTERN 1: FilterTabs
            ======================================== }}
        <Section class="bg-white rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhGridFour @size={{20}} class="text-violet-600" />
              <span>1. FilterTabs</span>
              <Chip @size="sm" @intent="primary">High Usage</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Horizontal pill-style filter buttons with active state and optional count badges.
              Great for filtering lists, tables, or card grids.
            </Text>

            {{! Demo }}
            <div class="bg-gray-50 rounded-lg p-6 mb-4">
              <FilterTabs
                @tabs={{SAMPLE_TABS}}
                @activeId={{this.activeTab}}
                @onSelect={{this.setActiveTab}}
              />
            </div>

            {{! Usage notes }}
            <div class="text-sm text-gray-600 bg-blue-50 rounded-lg p-4">
              <strong>Usage:</strong>
              Used in Explore Careers, Recommendations, Skills pages. Could accept
              <code>@tabs</code>,
              <code>@activeTab</code>,
              <code>@onSelect</code>
              args.
            </div>
          </:body>
        </Section>

        {{! ========================================
            PATTERN 2: MatchScoreBadge
            ======================================== }}
        <Section class="bg-white rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhTarget @size={{20}} class="text-violet-600" />
              <span>2. MatchScoreBadge</span>
              <Chip @size="sm" @intent="success">Core Pattern</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Circular percentage display with automatic color coding based on score thresholds.
              Used to show role matches, skill levels, and completion percentages.
            </Text>

            {{! Demo }}
            <div class="bg-gray-50 rounded-lg p-6 mb-4">
              <div class="flex items-end gap-8">
                {{#each (array 95 78 55 32) as |score|}}
                  <div class="text-center">
                    <MatchScoreBadge @score={{score}} @size="lg" />
                    <Text @intent="muted" @size="xs" class="mt-2">
                      {{#if (gte score 90)}}Excellent{{else if (gte score 70)}}Good{{else if
                        (gte score 50)
                      }}Fair{{else}}Low{{/if}}
                    </Text>
                  </div>
                {{/each}}
              </div>
            </div>

            <div class="text-sm text-gray-600 bg-blue-50 rounded-lg p-4">
              <strong>Usage:</strong>
              Accept
              <code>@score</code>
              (0-100),
              <code>@size</code>,
              <code>@label</code>. Auto-colors: 90+= green, 70+= violet, 50+= amber, &lt;50= red.
            </div>
          </:body>
        </Section>

        {{! ========================================
            PATTERN 3: StatCard
            ======================================== }}
        <Section class="bg-white rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhTrendUp @size={{20}} class="text-violet-600" />
              <span>3. StatCard</span>
              <Chip @size="sm" @intent="primary">High Usage</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Compact metric display with icon, value, label, and optional trend indicator. Perfect
              for dashboards and summary sections.
            </Text>

            {{! Demo }}
            <div class="bg-gray-50 rounded-lg p-6 mb-4">
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                  @icon={{PhCurrencyDollar}}
                  @value="$5,250"
                  @label="TA Available"
                  @trend="up"
                  @trendText="↑ Renewed"
                  @iconClass="text-green-600"
                />
                <StatCard
                  @icon={{PhCheckCircle}}
                  @value="68%"
                  @label="Plan Progress"
                  @trend="up"
                  @trendText="↑ 12% this month"
                  @iconClass="text-violet-600"
                  @trendClass="text-violet-600"
                />
                <StatCard
                  @icon={{PhBriefcase}}
                  @value="8"
                  @label="Open Roles"
                  @trend="stable"
                  @trendText="→ Stable"
                  @iconClass="text-blue-600"
                  @trendClass="text-amber-600"
                />
                <StatCard
                  @icon={{PhClock}}
                  @value="18mo"
                  @label="To Goal"
                  @trend="down"
                  @trendText="↓ 2mo faster"
                  @iconClass="text-amber-600"
                />
              </div>
            </div>

            <div class="text-sm text-gray-600 bg-blue-50 rounded-lg p-4">
              <strong>Usage:</strong>
              Accept
              <code>@icon</code>,
              <code>@value</code>,
              <code>@label</code>,
              <code>@trend</code>
              (up/down/stable),
              <code>@trendText</code>.
            </div>
          </:body>
        </Section>

        {{! ========================================
            PATTERN 4: SkillChip
            ======================================== }}
        <Section class="bg-white rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhSparkle @size={{20}} class="text-violet-600" />
              <span>4. SkillChip</span>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Skill tag with optional level indicator bar. Can show skill mastery at a glance.
            </Text>

            {{! Demo }}
            <div class="bg-gray-50 rounded-lg p-6 mb-4">
              <div class="flex flex-wrap gap-3">
                {{#each SAMPLE_SKILLS as |skill|}}
                  <SkillChip @name={{skill.name}} @level={{skill.level}} />
                {{/each}}
              </div>

              {{! Simple version }}
              <div class="mt-4 pt-4 border-t border-gray-200">
                <Text @size="sm" @intent="muted" class="mb-2">Simple version (no level):</Text>
                <div class="flex flex-wrap gap-2">
                  <SkillChip @name="Leadership" @intent="primary" @showLevel={{false}} />
                  <SkillChip @name="Patient Care" @intent="success" @showLevel={{false}} />
                  <SkillChip @name="Epic EMR" @intent="default" @showLevel={{false}} />
                  <SkillChip @name="Budget Management" @intent="warning" @showLevel={{false}} />
                </div>
              </div>
            </div>

            <div class="text-sm text-gray-600 bg-blue-50 rounded-lg p-4">
              <strong>Usage:</strong>
              Accept
              <code>@name</code>,
              <code>@level</code>
              (0-100),
              <code>@showLevel</code>.
            </div>
          </:body>
        </Section>

        {{! ========================================
            PATTERN 5: TaEligibilityBadge
            ======================================== }}
        <Section class="bg-white rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhGraduationCap @size={{20}} class="text-violet-600" />
              <span>5. TaEligibilityBadge</span>
              <Chip @size="sm" @intent="success">Brand Specific</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Tuition Assistance eligibility indicator with optional amount. Core to Tuition.io's
              value proposition.
            </Text>

            {{! Demo }}
            <div class="bg-gray-50 rounded-lg p-6 mb-4">
              <div class="flex flex-wrap gap-4">
                {{! Eligible with amount }}
                <TaEligibilityBadge @eligible={{true}} @amount="$2,400 covered" />

                {{! Eligible simple }}
                <TaEligibilityBadge @eligible={{true}} />

                {{! Partial coverage }}
                <TaEligibilityBadge
                  @eligible={{true}}
                  @partial={{true}}
                  @partialAmount="$1,200"
                  @totalAmount="$2,400"
                />

                {{! Not eligible }}
                <TaEligibilityBadge @eligible={{false}} />
              </div>
            </div>

            <div class="text-sm text-gray-600 bg-blue-50 rounded-lg p-4">
              <strong>Usage:</strong>
              Accept
              <code>@eligible</code>,
              <code>@amount</code>,
              <code>@partial</code>,
              <code>@partialAmount</code>.
            </div>
          </:body>
        </Section>

        {{! ========================================
            PATTERN 6: ActionableCard
            ======================================== }}
        <Section class="bg-white rounded-lg shadow-sm">
          <:header>
            <div class="flex items-center gap-2">
              <PhLayout @size={{20}} class="text-violet-600" />
              <span>6. ActionableCard</span>
              <Chip @size="sm" @intent="primary">Complex Pattern</Chip>
            </div>
          </:header>
          <:body>
            <Text @intent="muted" class="mb-4">
              Card layout with header badge, match score, metadata row, skill chips, and action
              buttons. Used for courses, certifications, and job listings.
            </Text>

            {{! Demo }}
            <div class="bg-gray-50 rounded-lg p-6 mb-4">
              <ActionableCard
                @title="Nurse Leadership Certificate"
                @matchScore={{98}}
                @showBanner={{true}}
                @bannerText="Top Pick for You"
                class="max-w-2xl"
              >
                <:badge>
                  <Chip @intent="success">Best Match</Chip>
                </:badge>

                <:metadata>
                  <div class="flex items-center gap-4 text-sm text-gray-500">
                    <span class="flex items-center gap-1">
                      <PhGraduationCap @size={{16}} />
                      ANCC
                    </span>
                    <span class="flex items-center gap-1">
                      <PhClock @size={{16}} />
                      4 months
                    </span>
                    <span class="flex items-center gap-1">
                      <PhCurrencyDollar @size={{16}} />
                      $1,850
                    </span>
                  </div>
                </:metadata>

                <:skills>
                  <Chip @size="sm">Leadership</Chip>
                  <Chip @size="sm">Team Management</Chip>
                  <Chip @size="sm">Quality Improvement</Chip>
                </:skills>

                <:eligibility>
                  <TaEligibilityBadge @eligible={{true}} @amount="$1,850 covered" />
                </:eligibility>

                <:actions>
                  <button
                    type="button"
                    class="flex items-center gap-1 text-gray-400 hover:text-pink-500 transition-colors"
                  >
                    <PhHeart @size={{20}} />
                    <span class="text-sm">Save</span>
                  </button>
                  <div class="flex gap-2">
                    <Button @intent="default" @appearance="outlined" @size="sm">
                      Learn More
                    </Button>
                    <Button
                      @intent="primary"
                      @size="sm"
                      @icon={{PhArrowRight}}
                      @label="Enroll Now"
                    />
                  </div>
                </:actions>
              </ActionableCard>
            </div>

            <div class="text-sm text-gray-600 bg-blue-50 rounded-lg p-4">
              <strong>Usage:</strong>
              This is a layout pattern combining multiple smaller components. Could be implemented
              as a compound component with named blocks.
            </div>
          </:body>
        </Section>

        {{! Summary }}
        <div class="bg-violet-50 rounded-xl p-6 border border-violet-100">
          <div class="flex items-start gap-4">
            <div
              class="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0"
            >
              <PhCode @size={{24}} class="text-violet-600" />
            </div>
            <div>
              <Heading @level={{3}} class="text-violet-900 mb-2">Ready for Extraction</Heading>
              <Text @intent="muted" class="mb-4">
                These patterns have been battle-tested across Career Pathways pages and are ready to
                be considered for extraction into tio-ui. Consistent use of these patterns ensures a
                cohesive user experience.
              </Text>
              <div class="flex flex-wrap gap-2">
                <Chip @intent="primary">FilterTabs</Chip>
                <Chip @intent="primary">MatchScoreBadge</Chip>
                <Chip @intent="primary">StatCard</Chip>
                <Chip @intent="primary">SkillChip</Chip>
                <Chip @intent="primary">TaEligibilityBadge</Chip>
                <Chip @intent="primary">ActionableCard</Chip>
              </div>
            </div>
          </div>
        </div>
      </div>
    </VStack>
  </template>
}
